/**
 * Native Python Analysis Engine with Pyodide WebAssembly integration & DataFrame Bridge.
 *
 * Pinned Pyodide Runtime (v0.26.2) executing real Python, Pandas, NumPy, and Matplotlib
 * directly against spreadsheet grid data in a sandboxed WebAssembly environment.
 */

export const PYODIDE_VERSION = '0.26.2';
export const PYODIDE_SELF_HOSTED_PATH = `/pyodide/v${PYODIDE_VERSION}/`;
/** @deprecated CDN loading is disabled; deploy Pyodide under /pyodide/v{version}/. */
export const PYODIDE_CDN_BASE = '';

/**
 * Resolves the primary Pyodide runtime URL.
 * Priority:
 * 1. window.__PYODIDE_BASE_URL__ or import.meta.env.VITE_PYODIDE_URL
 * 2. Self-hosted local path (/pyodide/v0.26.2/)
 * 3. CDN Fallback (if allowCdnFallback is true)
 */
export function getPyodideBaseUrl(_options?: { allowCdnFallback?: boolean | undefined }): string {
  if (typeof window !== 'undefined') {
    const win = window as unknown as { __PYODIDE_BASE_URL__?: string };
    if (win.__PYODIDE_BASE_URL__) {
      return win.__PYODIDE_BASE_URL__.endsWith('/') ? win.__PYODIDE_BASE_URL__ : `${win.__PYODIDE_BASE_URL__}/`;
    }
  }

  try {
    const meta = import.meta as unknown as { env?: { VITE_PYODIDE_URL?: string } };
    const envUrl = meta.env?.VITE_PYODIDE_URL;
    if (envUrl && typeof envUrl === 'string') {
      return envUrl.endsWith('/') ? envUrl : `${envUrl}/`;
    }
  } catch {
    // env not available
  }

  // Strictly return self-hosted local path
  return PYODIDE_SELF_HOSTED_PATH;
}

export interface PythonProgressState {
  phase: 'idle' | 'downloading' | 'verifying_integrity' | 'loading_pyodide' | 'loading_packages' | 'ready' | 'executing' | 'completed' | 'cancelled' | 'error';
  percentage: number;
  message: string;
}

export interface PythonExecutionResult {
  ok: boolean;
  stdout: string;
  stderr: string;
  result?: unknown;
  runtime: 'pyodide-wasm' | 'unavailable';
  runtimeUnavailable?: boolean;
  dataframe?: {
    columns: string[];
    rows: (string | number | boolean | null)[][];
    rowCount: number;
    colCount: number;
  };
  plotImageB64?: string;
  executionTimeMs: number;
  error?: string;
  cancelled?: boolean;
  timedOut?: boolean;
}

export interface SheetDataframeContext {
  name?: string;
  columns: string[];
  data: (string | number | boolean | null)[][];
}

export interface PyodideInterface {
  runPythonAsync: (code: string) => Promise<unknown>;
  runPython: (code: string) => unknown;
  loadPackage: (packages: string | string[]) => Promise<void>;
  globals: {
    set: (name: string, value: unknown) => void;
    get: (name: string) => unknown;
    delete?: (name: string) => void;
  };
  toJs?: (obj: unknown, options?: Record<string, unknown>) => unknown;
}

export interface PythonExecutionOptions {
  timeoutMs?: number | undefined;
  signal?: AbortSignal | undefined;
  requireRuntime?: boolean | undefined;
  loadPackages?: string[] | undefined;
  maxRows?: number | undefined;
  onProgress?: ((progress: PythonProgressState) => void) | undefined;
  allowCdnFallback?: boolean | undefined;
}

export class PythonEngine {
  private isLoaded = false;
  private isInitializing = false;
  private pyodide: PyodideInterface | null = null;
  private lastInitError: string | null = null;
  private loadedPackages = new Set<string>();
  private activeRuntimeUrl = PYODIDE_SELF_HOSTED_PATH;

  /**
   * Sets custom/mocked Pyodide instance for testing and containerized environments.
   */
  setPyodideInstance(instance: PyodideInterface | null): void {
    this.pyodide = instance;
    this.isLoaded = instance !== null;
    if (instance === null) {
      this.loadedPackages.clear();
    }
  }

  /**
   * Returns true if the Pyodide WebAssembly runtime is loaded and ready.
   */
  isRuntimeAvailable(): boolean {
    return this.isLoaded && this.pyodide !== null;
  }

  /**
   * Returns current Python runtime diagnostics and version.
   */
  getRuntimeStatus(): {
    available: boolean;
    runtime: 'pyodide-wasm' | 'unavailable';
    version: string;
    runtimeUrl: string;
    isInitializing: boolean;
    loadedPackages: string[];
    error?: string;
  } {
    return {
      available: this.isRuntimeAvailable(),
      runtime: this.isRuntimeAvailable() ? 'pyodide-wasm' : 'unavailable',
      version: PYODIDE_VERSION,
      runtimeUrl: this.activeRuntimeUrl,
      isInitializing: this.isInitializing,
      loadedPackages: Array.from(this.loadedPackages),
      ...(this.lastInitError ? { error: this.lastInitError } : {}),
    };
  }

  /**
   * Initializes the Pyodide WebAssembly runtime from self-hosted or fallback sources.
   */
  async initialize(options?: {
    onProgress?: ((progress: PythonProgressState) => void) | undefined;
    loadPyodideFn?: ((config: { indexURL: string }) => Promise<PyodideInterface>) | undefined;
    allowCdnFallback?: boolean | undefined;
  }): Promise<boolean> {
    if (this.isLoaded && this.pyodide) {
      options?.onProgress?.({ phase: 'ready', percentage: 100, message: `Pyodide v${PYODIDE_VERSION} runtime ready.` });
      return true;
    }
    if (this.isInitializing) return false;

    this.isInitializing = true;
    this.lastInitError = null;

    const runtimeBaseUrl = getPyodideBaseUrl({ allowCdnFallback: options?.allowCdnFallback });
    this.activeRuntimeUrl = runtimeBaseUrl;

    options?.onProgress?.({ phase: 'loading_pyodide', percentage: 20, message: `Initializing Pyodide v${PYODIDE_VERSION} from ${runtimeBaseUrl}...` });

    try {
      if (options?.loadPyodideFn) {
        options?.onProgress?.({ phase: 'downloading', percentage: 40, message: 'Downloading Pyodide WASM binaries...' });
        this.pyodide = await options.loadPyodideFn({ indexURL: runtimeBaseUrl });
        this.isLoaded = true;
        this.isInitializing = false;
        options?.onProgress?.({ phase: 'ready', percentage: 100, message: `Pyodide v${PYODIDE_VERSION} loaded successfully.` });
        return true;
      }

      if (typeof window !== 'undefined') {
        const win = window as unknown as { loadPyodide?: (config: { indexURL: string }) => Promise<PyodideInterface> };
        
        // Dynamically inject script tag if loadPyodide is not already in global scope
        if (typeof win.loadPyodide !== 'function' && typeof document !== 'undefined') {
          options?.onProgress?.({ phase: 'downloading', percentage: 30, message: `Fetching Pyodide bootstrapper from ${runtimeBaseUrl}...` });
          await new Promise<void>((resolve, reject) => {
            const scriptUrl = `${runtimeBaseUrl}pyodide.js`;
            const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
            if (existingScript) {
              existingScript.addEventListener('load', () => resolve());
              existingScript.addEventListener('error', () => reject(new Error(`Failed to load Pyodide bootstrapper from ${scriptUrl}`)));
              return;
            }
            const script = document.createElement('script');
            script.src = scriptUrl;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
              if (options?.allowCdnFallback && runtimeBaseUrl !== runtimeBaseUrl) {
                console.warn(`⚠️ Self-hosted Pyodide failed at ${scriptUrl}, falling back to CDN...`);
                script.onerror = () => reject(new Error(`Failed to load self-hosted Pyodide. CDN fallback is disabled.`));
              } else {
                reject(new Error(`Failed to load Pyodide bootstrapper from ${scriptUrl}`));
              }
            };
            document.head.appendChild(script);
          });
        }

        if (typeof win.loadPyodide === 'function') {
          options?.onProgress?.({ phase: 'downloading', percentage: 50, message: 'Downloading Pyodide WebAssembly core...' });
          this.pyodide = await win.loadPyodide({
            indexURL: runtimeBaseUrl,
          });
          this.isLoaded = true;
          this.isInitializing = false;
          options?.onProgress?.({ phase: 'ready', percentage: 100, message: `Pyodide v${PYODIDE_VERSION} initialized.` });
          return true;
        }
      }

      this.isInitializing = false;
      return false;
    } catch (err) {
      this.lastInitError = err instanceof Error ? err.message : String(err);
      this.isInitializing = false;
      options?.onProgress?.({ phase: 'error', percentage: 100, message: `Failed to initialize Pyodide: ${this.lastInitError}` });
      return false;
    }
  }

  /**
   * Loads Python packages (pandas, numpy, matplotlib, etc.) into Pyodide.
   */
  async loadPackages(packages: string[], onProgress?: (p: PythonProgressState) => void): Promise<boolean> {
    if (!this.pyodide) return false;
    try {
      const toLoad = packages.filter((pkg) => !this.loadedPackages.has(pkg));
      if (toLoad.length > 0) {
        onProgress?.({ phase: 'loading_packages', percentage: 60, message: `Loading Python packages: ${toLoad.join(', ')}...` });
        await this.pyodide.loadPackage(toLoad);
        for (const pkg of toLoad) {
          this.loadedPackages.add(pkg);
        }
      }
      onProgress?.({ phase: 'ready', percentage: 100, message: 'Packages loaded successfully.' });
      return true;
    } catch (err) {
      this.lastInitError = err instanceof Error ? err.message : String(err);
      return false;
    }
  }

  /**
   * Converts a 2D sheet cell matrix into a structured DataFrame context object.
   */
  createDataframeFromSheet(
    sheetData: (string | number | boolean | null)[][],
    hasHeader = true
  ): SheetDataframeContext {
    if (!sheetData || sheetData.length === 0) {
      return { columns: [], data: [] };
    }

    let columns: string[] = [];
    let rows: (string | number | boolean | null)[][] = [];

    if (hasHeader && sheetData.length > 0 && sheetData[0]) {
      columns = sheetData[0].map((c, i) => (c !== null && c !== undefined ? String(c).trim() : `col_${i + 1}`));
      rows = sheetData.slice(1);
    } else {
      const maxCols = Math.max(...sheetData.map((r) => r.length), 1);
      columns = Array.from({ length: maxCols }, (_, i) => `col_${i + 1}`);
      rows = sheetData;
    }

    return {
      columns,
      data: rows,
    };
  }

  /**
   * Converts a DataFrame result into a 2D matrix for direct spreadsheet insertion.
   */
  dataframeToSheetGrid(df: { columns: string[]; rows: (string | number | boolean | null)[][] }): (string | number | boolean | null)[][] {
    if (!df || !df.columns) return [];
    return [df.columns, ...df.rows];
  }

  /**
   * Executes Python code in real Pyodide WebAssembly runtime.
   * Enforces security sandboxing, row limits, timeout, cancellation, and stdout/stderr capture.
   * If runtime is unavailable, strictly returns runtime unavailable error with zero fake fallbacks.
   */
  async executePython(
    code: string,
    inputs: Record<string, SheetDataframeContext> = {},
    options?: PythonExecutionOptions
  ): Promise<PythonExecutionResult> {
    const startTime = performance.now();
    options?.onProgress?.({ phase: 'executing', percentage: 10, message: 'Preparing Python execution...' });

    const cleanCode = code.trim();
    if (!cleanCode) {
      return {
        ok: false,
        stdout: '',
        stderr: 'Error: Empty Python code provided.',
        runtime: this.isRuntimeAvailable() ? 'pyodide-wasm' : 'unavailable',
        executionTimeMs: 0,
      };
    }

    // 1. Security Sandbox: Restrict dangerous imports and network/OS execution
    const forbiddenModules = [
      'subprocess', 'socket', 'urllib', 'requests', 'http.client', 'http.server',
      'os.system', 'os.popen', 'os.spawn', 'shutil', 'ctypes', 'importlib'
    ];
    for (const mod of forbiddenModules) {
      const importPattern = new RegExp(`\\b(import\\s+${mod}|from\\s+${mod}\\s+import|__import__\\s*\\(\\s*['"]${mod}['"]\\))\\b`, 'i');
      if (importPattern.test(cleanCode)) {
        return {
          ok: false,
          stdout: '',
          stderr: `SecurityError: Access to module '${mod}' is restricted in sandboxed Python runtime.`,
          runtime: this.isRuntimeAvailable() ? 'pyodide-wasm' : 'unavailable',
          executionTimeMs: Number((performance.now() - startTime).toFixed(2)),
          error: `SecurityError: Access to module '${mod}' is restricted in sandboxed Python runtime.`,
        };
      }
    }

    // 2. Cancellation Check
    if (options?.signal?.aborted) {
      return {
        ok: false,
        stdout: '',
        stderr: 'Execution cancelled by user.',
        cancelled: true,
        runtime: this.isRuntimeAvailable() ? 'pyodide-wasm' : 'unavailable',
        executionTimeMs: 0,
        error: 'Execution cancelled by user.',
      };
    }

    // 3. Attempt initialization
    await this.initialize(options?.onProgress ? { onProgress: options.onProgress } : undefined);

    // 4. Strict Runtime Unavailable Check: NEVER fake or simulate execution
    if (!this.pyodide) {
      return {
        ok: false,
        stdout: '',
        stderr: 'Python Runtime Unavailable: Pyodide WebAssembly runtime is not loaded in current environment.',
        runtimeUnavailable: true,
        runtime: 'unavailable',
        executionTimeMs: Number((performance.now() - startTime).toFixed(2)),
        error: 'Python Runtime Unavailable: Pyodide WebAssembly runtime is not loaded in current environment.',
      };
    }

    const timeoutMs = options?.timeoutMs ?? 30000;
    const maxRows = options?.maxRows ?? 100000;

    // 5. Execute in real Pyodide WebAssembly instance
    try {
      if (options?.loadPackages && options.loadPackages.length > 0) {
        await this.loadPackages(options.loadPackages, options.onProgress);
      }

      // Initialize Pyodide IO redirect and Matplotlib backend
      const setupScript = `
import sys
import io

__sys_stdout_buf = io.StringIO()
__sys_stderr_buf = io.StringIO()
sys.stdout = __sys_stdout_buf
sys.stderr = __sys_stderr_buf

try:
    import matplotlib
    matplotlib.use('Agg')
    import matplotlib.pyplot as plt
except ImportError:
    plt = None
`;
      this.pyodide.runPython(setupScript);

      // Inject input DataFrames as real pandas DataFrames
      for (const [key, df] of Object.entries(inputs)) {
        const boundedRows = df.data.slice(0, maxRows);
        this.pyodide.globals.set(`__input_${key}_cols`, df.columns);
        this.pyodide.globals.set(`__input_${key}_data`, boundedRows);
        this.pyodide.runPython(`
try:
    import pandas as pd
    ${key} = pd.DataFrame(data=__input_${key}_data, columns=__input_${key}_cols)
except ImportError:
    ${key} = __input_${key}_data
`);
      }

      // Execute code asynchronously with timeout race
      const execPromise = this.pyodide.runPythonAsync(cleanCode);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`Python execution timed out after ${timeoutMs}ms`)), timeoutMs)
      );

      const rawResult = await Promise.race([execPromise, timeoutPromise]);

      // Extract stdout, stderr, plot, and DataFrame outputs
      const extractionScript = `
__captured_stdout = __sys_stdout_buf.getvalue()
__captured_stderr = __sys_stderr_buf.getvalue()

__plot_b64 = ""
if plt is not None and len(plt.get_fignums()) > 0:
    import base64
    buf = io.BytesIO()
    plt.savefig(buf, format='png', bbox_inches='tight')
    buf.seek(0)
    __plot_b64 = base64.b64encode(buf.read()).decode('utf-8')
    plt.close('all')

import json
__df_export = None
if 'df' in globals() and hasattr(df, 'to_json'):
    __df_export = df.to_json(orient='split')
`;
      this.pyodide.runPython(extractionScript);
      const capturedStdout = String(this.pyodide.globals.get('__captured_stdout') || '');
      const capturedStderr = String(this.pyodide.globals.get('__captured_stderr') || '');
      const capturedPlot = String(this.pyodide.globals.get('__plot_b64') || '');

      let outputDf: PythonExecutionResult['dataframe'] | undefined = undefined;
      const dfJson = this.pyodide.globals.get('__df_export');
      if (dfJson && typeof dfJson === 'string') {
        try {
          const parsed = JSON.parse(dfJson);
          outputDf = {
            columns: parsed.columns || [],
            rows: parsed.data || [],
            rowCount: (parsed.data || []).length,
            colCount: (parsed.columns || []).length,
          };
        } catch {
          // Non-json DataFrame export
        }
      }

      const executionTimeMs = Number((performance.now() - startTime).toFixed(2));
      options?.onProgress?.({ phase: 'completed', percentage: 100, message: 'Python execution finished.' });

      return {
        ok: true,
        stdout: capturedStdout || (rawResult !== undefined ? String(rawResult) : ''),
        stderr: capturedStderr,
        runtime: 'pyodide-wasm',
        result: rawResult,
        ...(outputDf ? { dataframe: outputDf } : {}),
        ...(capturedPlot ? { plotImageB64: capturedPlot } : {}),
        executionTimeMs,
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      const isTimeout = errorMsg.includes('timed out');
      return {
        ok: false,
        stdout: '',
        stderr: `Python Error: ${errorMsg}`,
        runtime: 'pyodide-wasm',
        timedOut: isTimeout,
        executionTimeMs: Number((performance.now() - startTime).toFixed(2)),
        error: errorMsg,
      };
    }
  }
}

export const pythonEngine = new PythonEngine();

if (typeof window !== 'undefined') {
  (window as unknown as { __pythonEngine: PythonEngine }).__pythonEngine = pythonEngine;
}
